import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import api, { fmtMoney, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import { Plus, Star, Search, Package as PkgIcon, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/rbac";
import ConfirmSoftDelete from "../components/ConfirmSoftDelete";

const emptyForm = {
    code: "", company_name: "", tax_number: "", email: "", phone: "",
    address: "", payment_term_days: 30, currency: "TRY", categories: "",
    preferred: false,
};

export default function Suppliers() {
    const { user } = useAuth();
    const canWrite = can(user, "masterdata.write");
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [toDelete, setToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/suppliers");
            setItems(r.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.code.trim() || !form.company_name.trim()) {
            toast.error("Kod ve firma adı zorunludur");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                categories: form.categories ? form.categories.split(",").map((s) => s.trim()) : [],
                payment_term_days: Number(form.payment_term_days),
            };
            await api.post("/suppliers", payload);
            toast.success("Tedarikçi oluşturuldu");
            setOpen(false);
            setForm(emptyForm);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const confirmDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/suppliers/${toDelete.id}`);
            toast.success("Tedarikçi geri dönüşüm kutusuna taşındı");
            setToDelete(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setDeleting(false); }
    };

    const filtered = items.filter((s) =>
        !q ||
        s.company_name?.toLowerCase().includes(q.toLowerCase()) ||
        s.code?.toLowerCase().includes(q.toLowerCase())
    );

    return (
        <AppShell
            title="Tedarikçiler"
            subtitle="Ana Veri"
            actions={
                canWrite ? (
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-supplier-btn" className="gap-2">
                            <Plus size={16} /> Yeni Tedarikçi
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>Yeni Tedarikçi</DialogTitle>
                            <DialogDescription>Tedarikçi bilgilerini girin</DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Kod *"><Input data-testid="sup-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
                            <Field label="Firma Adı *"><Input data-testid="sup-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></Field>
                            <Field label="Vergi No"><Input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} /></Field>
                            <Field label="E-posta"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                            <Field label="Telefon"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                            <Field label="Ödeme Vadesi (gün)"><Input type="number" value={form.payment_term_days} onChange={(e) => setForm({ ...form, payment_term_days: e.target.value })} /></Field>
                            <Field label="Para Birimi">
                                <select className="h-10 border border-input rounded-md px-3 bg-background text-sm" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                                    <option>TRY</option><option>USD</option><option>EUR</option><option>GBP</option>
                                </select>
                            </Field>
                            <Field label="Kategoriler (virgülle)"><Input value={form.categories} onChange={(e) => setForm({ ...form, categories: e.target.value })} placeholder="Metal, Elektronik" /></Field>
                            <Field label="Adres" full><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
                            <Button data-testid="save-supplier" onClick={submit} disabled={busy}>
                                {busy && <Loader2 size={14} className="mr-2 animate-spin"/>}
                                Kaydet
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                ) : null
            }
        >
            <div className="bg-card border border-border rounded-md">
                <div className="p-4 border-b border-border flex items-center gap-3">
                    <div className="relative w-72">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input data-testid="sup-search" placeholder="Ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
                    </div>
                    <div className="text-xs text-muted-foreground ml-auto">
                        {filtered.length} tedarikçi
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Kod</th>
                                <th>Firma</th>
                                <th>Kategori</th>
                                <th className="text-center">Vade</th>
                                <th className="text-center">Puan</th>
                                <th className="text-right">Teslimat %</th>
                                <th className="text-right">Kalite</th>
                                <th className="text-right">Fiyat Rekabeti</th>
                                <th>İletişim</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={9} className="text-center py-16 text-muted-foreground">
                                    <Loader2 size={22} className="mx-auto mb-2 animate-spin opacity-70"/>
                                    Yükleniyor...
                                </td></tr>
                            ) : filtered.map((s) => (
                                <tr key={s.id} data-testid={`sup-row-${s.code}`}>
                                    <td className="font-mono text-xs">
                                        <div className="flex items-center gap-1.5">
                                            {s.preferred && <Star size={12} className="text-amber-500 fill-amber-500" />}
                                            {s.code}
                                        </div>
                                    </td>
                                    <td className="font-medium">{s.company_name}</td>
                                    <td>
                                        <div className="flex flex-wrap gap-1">
                                            {(s.categories || []).map((c) => (
                                                <span key={c} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-secondary rounded">
                                                    {c}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="text-center tabular-nums">{s.payment_term_days} gün</td>
                                    <td className="text-center">
                                        <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                                            {(s.performance?.rating || 0).toFixed(1)}
                                            <Star size={12} className="fill-current" />
                                        </span>
                                    </td>
                                    <td className="text-right tabular-nums">{s.performance?.delivery_success_rate?.toFixed(0) || 0}%</td>
                                    <td className="text-right tabular-nums">{s.performance?.quality_score?.toFixed(0) || 0}</td>
                                    <td className="text-right tabular-nums">{s.performance?.price_competitiveness?.toFixed(0) || 0}</td>
                                    <td className="text-xs text-muted-foreground">
                                        <div>{s.email}</div>
                                        <div>{s.phone}</div>
                                    </td>
                                    <td className="text-right">
                                        {canWrite && (
                                            <button
                                                data-testid={`sup-delete-${s.code}`}
                                                onClick={() => setToDelete(s)}
                                                className="p-1.5 rounded hover:bg-rose-50 dark:hover:bg-rose-950/20 text-muted-foreground hover:text-rose-600"
                                                title="Geri dönüşüm kutusuna taşı"
                                            >
                                                <Trash2 size={14}/>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={10} className="text-center py-16 text-muted-foreground">
                                    <PkgIcon size={28} className="mx-auto mb-2 opacity-40" />
                                    Tedarikçi bulunamadı
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmSoftDelete
                open={!!toDelete}
                onOpenChange={(o) => !o && setToDelete(null)}
                onConfirm={confirmDelete}
                busy={deleting}
                itemLabel={toDelete ? `${toDelete.code} — ${toDelete.company_name}` : null}
                testid="confirm-delete-supplier"
            />
        </AppShell>
    );
}

function Field({ label, children, full }) {
    return (
        <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
            {children}
        </div>
    );
}
